import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const REGISTER_COPY = {
  fr: {
    serverError: "Impossible de contacter le serveur d'inscription. Veuillez réessayer dans quelques instants.",
    passwordMismatch: "Les mots de passe ne correspondent pas",
    shortPassword: "Le mot de passe doit contenir au moins 8 caractères",
    acceptTerms: "Veuillez accepter les conditions générales",
    success: "Compte créé avec succès!",
    title: "Inscription",
    subtitle: "Créez votre compte gratuitement",
    firstName: "Prénom",
    firstNamePlaceholder: "Jean",
    lastName: "Nom",
    lastNamePlaceholder: "Dupont",
    email: "Email",
    emailPlaceholder: "votre@email.com",
    club: "Club",
    clubPlaceholder: "Nom de votre club",
    password: "Mot de passe",
    passwordPlaceholder: "Min. 8 caractères",
    confirmPassword: "Confirmer le mot de passe",
    accountType: "Type de compte",
    player: "Joueur",
    clubType: "Club",
    termsStart: "J'accepte les",
    terms: "conditions générales",
    privacyMiddle: "et la",
    privacy: "politique de confidentialité",
    loading: "Création...",
    submit: "Créer mon compte",
    alreadyAccount: "Déjà un compte ?",
    login: "Se connecter",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
  },
  en: {
    serverError: "Unable to contact the registration server. Please try again in a moment.",
    passwordMismatch: "Passwords do not match",
    shortPassword: "Password must contain at least 8 characters",
    acceptTerms: "Please accept the terms and conditions",
    success: "Account created successfully!",
    title: "Sign up",
    subtitle: "Create your account for free",
    firstName: "First name",
    firstNamePlaceholder: "John",
    lastName: "Last name",
    lastNamePlaceholder: "Smith",
    email: "Email",
    emailPlaceholder: "your@email.com",
    club: "Club",
    clubPlaceholder: "Your club name",
    password: "Password",
    passwordPlaceholder: "Min. 8 characters",
    confirmPassword: "Confirm password",
    accountType: "Account type",
    player: "Player",
    clubType: "Club",
    termsStart: "I accept the",
    terms: "terms and conditions",
    privacyMiddle: "and the",
    privacy: "privacy policy",
    loading: "Creating...",
    submit: "Create my account",
    alreadyAccount: "Already have an account?",
    login: "Log in",
    showPassword: "Show password",
    hidePassword: "Hide password",
  },
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const getRegisterErrorMessage = (err, fallback) => {
  const apiMessage = formatApiErrorDetail(err.response?.data?.detail);
  if (apiMessage) return apiMessage;
  if (err.message && !["Failed to fetch", "Network Error"].includes(err.message)) return err.message;
  return fallback;
};

const RegisterPage = () => {
  const { register } = useAuth();
  const { language } = useLanguage();
  const copy = REGISTER_COPY[language] || REGISTER_COPY.fr;
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    club: "",
    password: "",
    confirmPassword: "",
    account_type: "player"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(copy.passwordMismatch);
      return;
    }

    if (formData.password.length < 8) {
      toast.error(copy.shortPassword);
      return;
    }

    if (!acceptTerms) {
      toast.error(copy.acceptTerms);
      return;
    }

    setLoading(true);

    try {
      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        club: formData.club,
        password: formData.password,
        account_type: formData.account_type
      });
      toast.success(copy.success);
      navigate("/dashboard");
    } catch (err) {
      console.error("Registration failed", err);
      toast.error(getRegisterErrorMessage(err, copy.serverError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      <div className="min-h-screen flex items-center justify-center px-6 py-32">
        <div className="w-full max-w-md">
          <div className="bg-[#18181B] border border-white/10 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black uppercase text-white mb-2">{copy.title}</h1>
              <p className="text-zinc-500">{copy.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-white text-sm font-medium">{copy.firstName}</Label>
                  <Input
                    id="first_name"
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                    placeholder={copy.firstNamePlaceholder}
                    data-testid="register-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-white text-sm font-medium">{copy.lastName}</Label>
                  <Input
                    id="last_name"
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                    placeholder={copy.lastNamePlaceholder}
                    data-testid="register-lastname"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-white text-sm font-medium">{copy.email}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                  placeholder={copy.emailPlaceholder}
                  data-testid="register-email"
                />
              </div>

              <div>
                <Label htmlFor="club" className="text-white text-sm font-medium">{copy.club}</Label>
                <Input
                  id="club"
                  type="text"
                  value={formData.club}
                  onChange={(e) => setFormData({ ...formData, club: e.target.value })}
                  className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                  placeholder={copy.clubPlaceholder}
                  data-testid="register-club"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-white text-sm font-medium">{copy.password}</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] pr-10"
                    placeholder={copy.passwordPlaceholder}
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    aria-label={showPassword ? copy.hidePassword : copy.showPassword}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-white text-sm font-medium">{copy.confirmPassword}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                  placeholder="••••••••"
                  data-testid="register-confirm-password"
                />
              </div>

              <div>
                <Label className="text-white text-sm font-medium mb-3 block">{copy.accountType}</Label>
                <RadioGroup
                  value={formData.account_type}
                  onValueChange={(value) => setFormData({ ...formData, account_type: value })}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="player" id="player" className="border-white/30 text-[#CCFF00]" />
                    <Label htmlFor="player" className="text-zinc-300 cursor-pointer">{copy.player}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="club" id="club" className="border-white/30 text-[#CCFF00]" />
                    <Label htmlFor="club" className="text-zinc-300 cursor-pointer">{copy.clubType}</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  className="border-white/30 data-[state=checked]:bg-[#CCFF00] data-[state=checked]:border-[#CCFF00] mt-0.5"
                  data-testid="register-terms"
                />
                <Label htmlFor="terms" className="text-sm text-zinc-400 cursor-pointer leading-relaxed">
                  {copy.termsStart}{" "}
                  <a href="#" className="text-[#CCFF00] hover:underline">{copy.terms}</a>
                  {" "}{copy.privacyMiddle}{" "}
                  <a href="#" className="text-[#CCFF00] hover:underline">{copy.privacy}</a>
                </Label>
              </div>

              <button
                type="submit"
                disabled={loading || !acceptTerms}
                className="w-full bg-[#CCFF00] text-black py-3 font-bold uppercase tracking-wide hover:bg-[#AACC00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit"
              >
                {loading ? copy.loading : copy.submit}
              </button>
            </form>

            <p className="text-center mt-6 text-zinc-500 text-sm">
              {copy.alreadyAccount}{" "}
              <Link to="/login" className="text-[#CCFF00] hover:underline" data-testid="register-login-link">
                {copy.login}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterPage;
